import {
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { paramCase } from 'change-case';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { identifierByConfig } from 'src/utils/fhir';
import uuidv4 from 'src/utils/uuidv4';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import CustomLink from 'src/careflow/common/CustomLink';

type Props = {
  row: WrappedOrganization;
  customLink?: boolean;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction | null;
  menuItems?: JSX.Element;
  translate?: any;
};

export default function FhirOrganizationTableRow({
  row,
  customLink,
  onEditRow,
  onDeleteRow,
  menuItems,
  translate,
}: Props) {
  const { componentsData } = useTenantConfigData();
  const { list } = componentsData?.organization ?? {};

  const [openMenu, setOpenMenu] = useState<HTMLElement | null>(null);

  const { id, name, type, identifier, active } = row;

  const isAllowedToDelete = checkAclValidation({
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.DELETE],
  });

  const isAllowedToView = checkAclValidation({
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.VIEW],
  });

  const mapIdentifiers = identifierByConfig(identifier ?? null, list);

  return (
    <TableRow hover>
      <TableCell>
        {customLink ? (
          <CustomLink to={`${PATH_DASHBOARD.fhirOrganization.details(paramCase(id!))}`}>
            <Typography noWrap variant="subtitle2">
              {name}
            </Typography>
          </CustomLink>
        ) : (
          <TableCell>
            <Typography variant="subtitle2" noWrap>
              {name}
            </Typography>
          </TableCell>
        )}
      </TableCell>
      <TableCell>
        {mapIdentifiers?.map((i) => (
          <Tooltip title={i?.tooltip} key={uuidv4()}>
            <Typography variant="body2" display="flex" alignItems="center">
              <Icon icon={i?.icon} style={{ ...i?.properties }} />
              {i?.value ? i?.value : null}
            </Typography>
          </Tooltip>
        ))}
      </TableCell>
      <TableCell>
        {type?.map((e, index) => (
          <Chip key={uuidv4()} label={e?.text ?? e?.coding?.[0]?.display} />
        ))}
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2" noWrap>
          {row?.getPhones()?.[0]}
        </Typography>
        <Typography variant="caption" noWrap>
          {row?.getEmails()?.[0]}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" flexWrap="wrap">
          {row?.getFormattedAddresses()}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <>
          <IconButton
            onClick={(event) => setOpenMenu(event.currentTarget)}
            sx={{
              color: '#637381',
              '&:hover': {
                backgroundColor: 'rgba(99, 115, 129, 0.08)',
              },
            }}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>

          <Menu
            anchorEl={openMenu}
            open={Boolean(openMenu)}
            onClose={() => setOpenMenu(null)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {menuItems ? menuItems : null}
            {isAllowedToDelete && onDeleteRow && (
              <MenuItem
                onClick={onDeleteRow}
                disabled={active === false}
                sx={{ color: 'error.main' }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Iconify icon="eva:trash-2-outline" sx={{ fontSize: 20 }} />
                  <Typography variant="body2">{translate('list.delete')}</Typography>
                </Stack>
              </MenuItem>
            )}
            {isAllowedToView && (
              <MenuItem onClick={onEditRow}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Iconify icon="eva:edit-fill" sx={{ fontSize: 20 }} />
                  <Typography variant="body2">{translate('list.details')}</Typography>
                </Stack>
              </MenuItem>
            )}
          </Menu>
        </>
      </TableCell>
    </TableRow>
  );
}
